import { TriangleAlert } from 'lucide-react';

import type { GetGapExecutionApiResponse } from '@momentum/types';

import { Prose } from './Prose';

/**
 * What the execution covered, and what it could not adjudicate.
 *
 * Two levels are shown separately because they answer different questions. The
 * run-level overview says how many scopes were analyzed and which detectors ran;
 * each scope's own coverage says what its finders reached, which gaps arrived
 * unadjudicated, and which validator batches failed. Both are duplicated into
 * every scope document, so a reader would otherwise have to know which copy to
 * trust.
 */
export function RunCoverageSection({
  execution,
}: {
  execution: GetGapExecutionApiResponse;
}) {
  const { coverage, recurringPatterns } = execution.overview;

  const pendingGapIds = execution.scopes.flatMap(
    (scope) => scope.coverage.pendingValidationGapIds
  );
  const failedBatches = execution.scopes.flatMap(
    (scope) => scope.coverage.failedValidatorBatches
  );
  const nothingUnadjudicated =
    pendingGapIds.length === 0 &&
    failedBatches.length === 0 &&
    recurringPatterns.length === 0;

  return (
    <section>
      <div className="gr-rule gr-rule-flush">
        <span>Coverage</span>
        <i />
      </div>

      <div className="gr-cols2">
        <div className="gr-stack">
          <div className="gr-card">
            <div className="gr-kv">
              <span>scopesAnalyzed</span>
              <span>{coverage.scopesAnalyzed}</span>
            </div>
            <div className="gr-kv">
              <span>scopesFailed</span>
              <span>{coverage.scopesFailed}</span>
            </div>
          </div>

          <div className="gr-card">
            <span className="gr-card-k">detectorsNote</span>
            <div className="gr-card-v">
              <Prose text={coverage.detectorsNote} />
            </div>
          </div>

          {execution.scopes.map((scope) => (
            <div className="gr-card" key={scope.scopeExternalId}>
              <span className="gr-card-k">
                coverageNotes · {scope.scopeName} ·{' '}
                <span className="mono">{scope.coverage.slug}</span>
              </span>
              <div className="gr-card-v">
                {scope.coverage.coverageNotes.map((note) => (
                  <Prose key={note.slice(0, 48)} text={note} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="gr-stack">
          <div className="gr-card">
            <span className="gr-card-k">consistencyPass.result</span>
            <div className="gr-card-v">
              <Prose text={execution.consistencyPass.result} />
            </div>
          </div>

          {nothingUnadjudicated ? (
            <div className="gr-hole gr-hatch gr-hole-flat" data-owned="false">
              <span className="gr-hole-text">
                No failed validator batches, no gaps left pending validation,
                and no recurring patterns — all three are empty in this
                execution.
              </span>
            </div>
          ) : (
            <>
              {pendingGapIds.length > 0 && (
                <div className="gr-card">
                  <span className="gr-card-k">
                    pendingValidationGapIds · {pendingGapIds.length}
                  </span>
                  <ul className="gr-filelist">
                    {pendingGapIds.map((gapId) => (
                      <li key={gapId}>{gapId}</li>
                    ))}
                  </ul>
                </div>
              )}

              {failedBatches.map((batch) => (
                <div
                  className="gr-cand"
                  key={`${batch.batchNumber}-${batch.reason.slice(0, 24)}`}
                >
                  <div className="gr-cand-top">
                    <span className="gr-chip gr-chip-lg">
                      batch {batch.batchNumber}
                    </span>
                    <span className="gr-chip" data-tone="rose">
                      {batch.gapIds.length} unadjudicated
                    </span>
                  </div>
                  <span className="gr-cand-k">reason</span>
                  <p className="gr-cand-reason">{batch.reason}</p>
                  <span className="gr-cand-k">gapIds</span>
                  <p className="gr-cand-claim">{batch.gapIds.join(', ')}</p>
                </div>
              ))}

              {recurringPatterns.map((pattern) => (
                <div className="gr-cand" key={pattern.pattern.slice(0, 48)}>
                  <div className="gr-cand-top">
                    <span className="gr-chip">recurring pattern</span>
                    <span className="gr-chip">
                      {pattern.affectedGapIds.length} affected
                    </span>
                  </div>
                  <span className="gr-cand-k">pattern</span>
                  <p className="gr-cand-reason">{pattern.pattern}</p>
                  {pattern.whatHappened !== undefined && (
                    <>
                      <span className="gr-cand-k">whatHappened</span>
                      <p className="gr-cand-claim">{pattern.whatHappened}</p>
                    </>
                  )}
                  {pattern.documentedCost !== undefined && (
                    <>
                      <span className="gr-cand-k gr-stack-top-sm">
                        documentedCost
                      </span>
                      <p className="gr-cand-claim">{pattern.documentedCost}</p>
                    </>
                  )}
                </div>
              ))}
            </>
          )}

          <div className="gr-note" data-tone="cyan">
            <TriangleAlert />
            <div>
              Report file paths (<code>finderReports</code>,{' '}
              <code>validationReports</code>, <code>reportPath</code>) are
              absent from the response type, not merely unrendered — they point
              into <code>reports/run-N/…</code> on whichever machine ran the
              workflow, so no client could open them.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
